import React from "react";
import moment from "moment";
import { CompanySidePanel } from "components/SidePanel/Company/CompanySidePanel";
import SmallDetails from "components/SmallDetails/SmallDetails";
import { RenderEditOptions, Section } from "components/MainSectionTemplate/types";
import { FormatArrivalTime } from "flights/lookup/FlightSearchResult";
import { FlightDesignatorInput } from "flights/FlightDesignatorInput";
import { getAirportCode } from "shared/utils/flightUtils";
import { formatTime, formatWrittenDayMonthYear } from "utilities";
import { OptionalTextAreaDetails } from "components/ValidatedFields/OptionalTextAreaInput";
import OptionalTextAreaInput from "components/ValidatedFields/OptionalTextAreaInput";
import OptionalTextInput from "components/ValidatedFields/OptionalTextInput";
import ArtistModalPicker from "components/Picker/ArtistPicker/ArtistModalPicker";
import AirlinePaginatedDropdown from "components/dropdowns/paginated/AirlinePaginatedDropdown";
import AirportPaginatedDropdown from "components/dropdowns/paginated/AirportPaginatedDropdown";
import { DateTimePicker } from "components/DatePicker/DateTimePicker";
import { FlightFromAiInfo } from "./useExtractorSessionDetail";

export const extractorSessionSections = (
  state: FlightFromAiInfo,
  getUpdateExtractedFlightInstruction: (f: FlightFromAiInfo) => server.dto.UpdateExtractedFlightInstruction,
): Array<Section> => [{
  key: "flight",
  type: "regular",
  title: "Flight from AI",
  fields: [{
    key: "artist",
    label: site.currentEnvironment.functionGroups.artist.singular,
    isEmpty: !state.artist,
    render: () => <CompanySidePanel type="artist"
      companyId={state.artist.artistId}
      trigger={<a>{state.artist.name}</a>}
      fallbackIfNoPermission={<>{state.artist.name}</>}
    />,
    inPageEdit: {
      hoverEdit: true,
      render: ({ onChange, cancel, value }) => <div className="main-section-template-fields-padding">
        <ArtistModalPicker
          inPageEditing={{ onChange, cancel, hasValue: Boolean(value) }}
          securityScope="limitByUserAccess"
          canCreateNew={false}
        />
      </div>,
      fullWidth: false,
      type: "custom",
      initialValue: state.artist,
      submitChange: async (artist: server.dto.ArtistSmall) => window.CoreApi.Extractor.UpdateExtractedFlight(
        getUpdateExtractedFlightInstruction({ ...state, artist }),
      ),
    },
  }, {
    key: "travelAgency",
    label: RESX.Flight.TravelAgency,
    isEmpty: !state.travelAgency,
    render: () => <div>{state.travelAgency}</div>,
    inPageEdit: {
      render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <OptionalTextInput
        focusOnMount
        maxLength={serverReference.constants.flight.maxTravelAgencyLength}
        value={value}
        onChange={(v) => { onChange(v); }}
        onEnter={() => submit()}
        onEsc={() => cancel()}
      />,
      fullWidth: true,
      type: "confirmOrCancelButton",
      initialValue: state.travelAgency ?? "",
      submitChange: (travelAgency: string) => window.CoreApi.Extractor.UpdateExtractedFlight(
        getUpdateExtractedFlightInstruction({ ...state, travelAgency }),
      ),
    },
  }, {
    key: "notes",
    label: RESX.Note.Notes,
    isEmpty: !state.notes,
    render: () => <OptionalTextAreaDetails>{state.notes}</OptionalTextAreaDetails>,
    inPageEdit: {
      render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <OptionalTextAreaInput
        focusOnMount
        maxLength={serverReference.constants.flight.maxNotesLength}
        rows={3}
        value={value}
        onChange={(v) => { onChange(v); }}
        onCtrlEnter={() => submit()}
        onEsc={() => cancel()}
      />,
      fullWidth: true,
      type: "confirmOrCancelButton",
      initialValue: state.notes ?? "",
      submitChange: (notes: string) => window.CoreApi.Extractor.UpdateExtractedFlight(
        getUpdateExtractedFlightInstruction({ ...state, notes }),
      ),
    },
  }, {
    key: "airline",
    label: RESX.Flight.Airline,
    isEmpty: !state.airline,
    render: () => <SmallDetails
      label={state.airline.name}
    />,
    inPageEdit: {
      render: ({ value, onChange, cancel }: RenderEditOptions<server.dto.Airline>) => <AirlinePaginatedDropdown
        displayFriendyCodeInSelectedValue={false}
        value={value}
        onChange={(airline) => {
          onChange(airline, true);
        }}
        onEsc={() => cancel()}
        config={{
          focusOnMount: true,
          isSearchable: true,
          openMenuOnFocus: true,
        }}
      />,
      fullWidth: true,
      type: "custom",
      initialValue: state.airline,
      submitChange: (airline: server.dto.Airline) => window.CoreApi.Extractor.UpdateExtractedFlight(
        getUpdateExtractedFlightInstruction({ ...state, airline }),
      ),
    },
  }, {
    key: "flightDesignator",
    label: RESX.Flight.FlightNumber,
    isEmpty: !state.flightDesignator,
    render: () => <div>{state.flightDesignator}</div>,
    inPageEdit: {
      render: ({ value, onChange, cancel, submit }: RenderEditOptions<string>) => <FlightDesignatorInput
        value={value}
        placeholderAirline={state.airline?.iata || state.airline?.icao || ""}
        validatorKey="flightCode"
        onChange={(fd) => onChange(fd)}
        focusOnMount
        onEsc={() => cancel()}
        onEnter={() => submit()}
      />,
      initialValue: state.flightDesignator,
      fullWidth: false,
      type: "confirmOrCancelButton",
      submitChange: (flightDesignator: string) => window.CoreApi.Extractor.UpdateExtractedFlight(
        getUpdateExtractedFlightInstruction({ ...state, flightDesignator }),
      ),
    },
  }],
}, {
  key: "departureArrival",
  title: "",
  hideTitle: true,
  type: "regular",
  fields: [{
    key: "departureArrivalLabel",
    label: "",
    left: {
      key: "departureLabel",
      render: () => <h3>{RESX.Flight.Departure}</h3>
    },
    right: {
      key: "arrivalLabel",
      render: () => <h3>{RESX.Flight.Arrival}</h3>
    }
  }, {
    key: "date",
    label: RESX.DateTime.lblDate,
    left: {
      key: "departureLocal",
      render: () => <div className="flex-column">
        <div>{formatTime(state.departureLocal)}</div>
        <div>{formatWrittenDayMonthYear(state.departureLocal)}</div>
      </div>,
      inPageEdit: {
        render: ({ value, onChange, submit, cancel }: RenderEditOptions<moment.Moment>) => <DateTimePicker
          value={value}
          onChange={v => onChange(v)}
          validatorKey="departureDate"
          minDate={moment(serverReference.constants.appointment.minDate)}
          maxDate={moment(serverReference.constants.appointment.maxDate)}
          onEnter={submit}
          onEsc={cancel}
        />,
        type: "confirmOrCancelButton",
        fullWidth: true,
        initialValue: state.departureLocal,
        submitChange: (departureLocal: moment.Moment) => {
          const diff = departureLocal.diff(state.departureLocal, "minutes");
          const arrivalLocal = state.arrivalLocal.clone().add(diff, "minutes");
          return window.CoreApi.Extractor.UpdateExtractedFlight(
            getUpdateExtractedFlightInstruction({ ...state, departureLocal, arrivalLocal }),
          );
        },
      },
    },
    right: {
      key: "arrivalLocal",
      render: () => <FormatArrivalTime departure={state.departureLocal} arrival={state.arrivalLocal} />,
      inPageEdit: {
        render: ({ value, onChange, submit, cancel }: RenderEditOptions<moment.Moment>) => <DateTimePicker
          value={value}
          onChange={v => onChange(v)}
          validatorKey="arrivalDate"
          minDate={state.departureLocal.clone().add(serverReference.constants.flight.maxArrivalBeforeDepartureHours, "hours")}
          maxDate={state.departureLocal.clone().add(serverReference.constants.flight.maxArrivalAfterDepartureHours, "hours")}
          onEnter={submit}
          onEsc={cancel}
        />,
        type: "confirmOrCancelButton",
        fullWidth: true,
        initialValue: state.arrivalLocal,
        submitChange: (arrivalLocal: moment.Moment) => window.CoreApi.Extractor.UpdateExtractedFlight(
          getUpdateExtractedFlightInstruction({ ...state, arrivalLocal }),
        ),
      },
    },
  }, {
    key: "airport",
    label: RESX.Flight.Airport,
    left: {
      key: "departureAirport",
      isEmpty: !state.departureAirport,
      render: () => <SmallDetails
        label={`${state.departureAirport.name} (${getAirportCode(state.departureAirport)})`}
        subLabel={state.departureAirport.location}
      />,
      inPageEdit: {
        render: ({ value, onChange, cancel }: RenderEditOptions<server.dto.Airport>) => <AirportPaginatedDropdown
          value={value}
          onChange={(airport) => onChange(airport, true)}
          onEsc={() => cancel()}
          config={{
            focusOnMount: true,
            isSearchable: true,
            openMenuOnFocus: true,
          }}
        />,
        type: "custom",
        initialValue: state.departureAirport,
        fullWidth: true,
        submitChange: (departureAirport: server.dto.Airport) => window.CoreApi.Extractor.UpdateExtractedFlight(
          getUpdateExtractedFlightInstruction({ ...state, departureAirport }),
        ),
      },
    },
    right: {
      key: "arrivalAirport",
      isEmpty: !state.arrivalAirport,
      render: () => <SmallDetails
        label={`${state.arrivalAirport.name} (${getAirportCode(state.arrivalAirport)})`}
        subLabel={state.arrivalAirport.location}
      />,
      inPageEdit: {
        render: ({ value, onChange, cancel }: RenderEditOptions<server.dto.Airport>) => <AirportPaginatedDropdown
          value={value}
          onChange={(airport) => onChange(airport, true)}
          onEsc={() => cancel()}
          config={{
            focusOnMount: true,
            isSearchable: true,
            openMenuOnFocus: true,
          }}
        />,
        type: "custom",
        initialValue: state.arrivalAirport,
        fullWidth: true,
        submitChange: (arrivalAirport: server.dto.Airport) => window.CoreApi.Extractor.UpdateExtractedFlight(
          getUpdateExtractedFlightInstruction({ ...state, arrivalAirport }),
        ),
      },
    },
  }, {
    key: "terminal",
    label: RESX.Flight.Terminal,
    left: {
      key: "departureTerminal",
      isEmpty: !state.departureTerminal,
      render: () => <div>{state.departureTerminal}</div>,
      inPageEdit: {
        render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <OptionalTextInput
          focusOnMount
          width={60}
          maxLength={serverReference.constants.flight.maxTerminalLength}
          value={value}
          onChange={(v) => { onChange(v); }}
          onEnter={() => submit()}
          onEsc={() => cancel()}
        />,
        fullWidth: false,
        type: "confirmOrCancelButton",
        initialValue: state.departureTerminal ?? "",
        submitChange: (departureTerminal: string) => window.CoreApi.Extractor.UpdateExtractedFlight(
          getUpdateExtractedFlightInstruction({ ...state, departureTerminal }),
        ),
      },
    },
    right: {
      key: "arrivalTerminal",
      isEmpty: !state.arrivalTerminal,
      render: () => <div>{state.arrivalTerminal}</div>,
      inPageEdit: {
        render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <OptionalTextInput
          focusOnMount
          width={60}
          maxLength={serverReference.constants.flight.maxTerminalLength}
          value={value}
          onChange={(v) => { onChange(v); }}
          onEnter={() => submit()}
          onEsc={() => cancel()}
        />,
        fullWidth: false,
        type: "confirmOrCancelButton",
        initialValue: state.arrivalTerminal ?? "",
        submitChange: (arrivalTerminal: string) => window.CoreApi.Extractor.UpdateExtractedFlight(
          getUpdateExtractedFlightInstruction({ ...state, arrivalTerminal }),
        ),
      },
    },
  }],
}]
